import { useState, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API, AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [sendingReset, setSendingReset] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/login`, formData);
      
      if (response.data.user.role !== "parent") {
        toast.error("Please use admin login");
        setLoading(false);
        return;
      }
      
      login(response.data.token, response.data.user);
      toast.success("Login successful!");
      navigate('/application');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail) {
      toast.error("Please enter your email address");
      return;
    }

    setSendingReset(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      toast.success("Password reset link sent to your email");
      setShowForgotPassword(false);
      setForgotEmail("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reset email");
    } finally {
      setSendingReset(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0E366A] via-[#1a4d7a] to-[#0E366A] flex flex-col">
      {/* School Header */}
      <div className="bg-[#F7B538] py-2 px-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="text-sm text-[#0E366A] font-medium">Welcome to London Islamic School</div>
        </div>
      </div>
      
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          {/* School Logo and Tagline */}
          <div className="text-center mb-8">
            <img 
              src="/lis_logo.png" 
              alt="London Islamic School Logo" 
              className="mx-auto mb-4 h-32 w-auto"
            />
            <h1 className="text-3xl font-serif text-white mb-2">London Islamic School</h1>
            <p className="text-[#F7B538] text-sm italic">Learning today. Leading tomorrow. Inspired Forever.</p>
          </div>

          <Button 
            data-testid="back-btn"
            variant="ghost"
            className="text-white hover:text-[#F7B538] hover:bg-white/10 mb-4" 
            onClick={() => navigate('/')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        
          <Card className="shadow-2xl border-0 bg-white/95 backdrop-blur">
            <CardHeader className="space-y-1 bg-[#0E366A] text-white rounded-t-lg">
              <CardTitle className="text-2xl font-bold text-center">Parent Login</CardTitle>
              <CardDescription className="text-center text-white/90">Login to submit or track your application</CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input 
                    data-testid="email-input"
                    id="email" 
                    name="email" 
                    type="email" 
                    required 
                    value={formData.email}
                    onChange={handleChange}
                    className="border-[#0E366A]/20 focus:border-[#F7B538]"
                  />
                </div>
              
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input 
                    data-testid="password-input"
                    id="password" 
                    name="password" 
                    type="password" 
                    required 
                    value={formData.password}
                    onChange={handleChange}
                    className="border-[#0E366A]/20 focus:border-[#F7B538]"
                  />
                  <div className="text-right">
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-sm text-[#0E366A] hover:text-[#F7B538] hover:underline"
                    >
                      Forgot Password?
                    </button>
                  </div>
                </div>
              
                <Button 
                  data-testid="login-submit-btn"
                  type="submit" 
                  className="w-full bg-[#F7B538] hover:bg-[#e5a627] text-[#0E366A] font-semibold" 
                  disabled={loading}
                >
                  {loading ? "Logging in..." : "Login"}
                </Button>
              </form>
            
              <p className="text-center text-sm text-gray-600 mt-4">
                Don't have an account?{" "}
                <Link to="/register" className="text-[#0E366A] hover:text-[#F7B538] font-semibold hover:underline">
                  Register here
                </Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-[#0E366A]">Reset Password</DialogTitle>
            <DialogDescription>
              Enter your email address and we'll send you a link to reset your password.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="forgot-email">Email Address</Label>
              <Input
                id="forgot-email"
                type="email"
                placeholder="Enter your email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                className="border-[#0E366A]/20 focus:border-[#F7B538]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowForgotPassword(false);
                setForgotEmail("");
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleForgotPassword}
              disabled={sendingReset}
              className="bg-[#F7B538] hover:bg-[#e5a627] text-[#0E366A] font-semibold"
            >
              {sendingReset ? "Sending..." : "Send Reset Link"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoginPage;